import type { User } from '@lmc/ts-common'
import { Add, Banner, Button, Form, FormCard, PageHeader } from '@lmc/ui-kit'
import { colours } from '@lmc/ui-kit/src/config/theme'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Switch } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { getNominative, showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'

import dayjs, { type Dayjs } from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import cloneDeep from 'lodash/cloneDeep'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { PageWrapper } from '../components'
import { handleOnFinishFailed } from '../formComponents/utils'
import {
  useGetInvoicingSettings,
  useUpdateInvoicingSettings
} from '../hooks/useInvoicingRates'
import {
  OverrideLevel,
  type PaymentCard,
  type PaymentCardDto,
  type PaymentCardTeamMember,
  RateType,
  useDeletePayRateCardFutureVersion,
  useGetPaymentCards,
  useGetPayRatesSettings,
  useUpdatePaymentCards,
  useUpdatePayRatesSettings
} from '../hooks/usePaymentCards'
import FixedFooter from './components/FixedFooter'
import PayRateCard from './components/PayRateCard'

export type RateEntry = {
  level: OverrideLevel
  eventTypeId: string | null
  rate: number
  rateType: RateType
  isDeleted?: boolean
}

export type PayRateCardForm = {
  _id?: string
  initialId?: string
  name: string
  effectiveDate?: Dayjs
  effectiveTime?: Dayjs
  rates: RateEntry[]
  isDeleted?: boolean
  plannedUpdateRemoved?: boolean
  previousVersion?: PaymentCard
  teamMembers?: PaymentCardTeamMember[]
}

type PayRatesSettingsForm = {
  payRates: PayRateCardForm[]
}

const HELP_ARTICLE_URL = 'https://support.logmycare.co.uk'

const parseCards = (cards: PaymentCard[]): PayRateCardForm[] =>
  cards.map(
    ({
      _id,
      initialId,
      name,
      effectiveFrom,
      rates,
      previousVersion,
      teamMembers
    }) => {
      const effectiveDateTime = effectiveFrom ? dayjs(effectiveFrom) : undefined

      return {
        _id,
        initialId,
        name,
        effectiveDate: effectiveDateTime,
        effectiveTime: effectiveDateTime,
        previousVersion,
        teamMembers,
        rates: rates.map((entry) => ({
          ...entry,
          rate: parseFloat(entry.rate) || 0,
          rateType: entry.rateType || RateType.HOURLY
        }))
      }
    }
  )

const buildEffectiveFrom = (card: PayRateCardForm) => {
  if (!card.effectiveDate || !card.effectiveTime) {
    return undefined
  }

  return dayjs(card.effectiveDate)
    .set('hour', dayjs(card.effectiveTime).hour())
    .set('minute', dayjs(card.effectiveTime).minute())
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString()
}

const cardToDto = (card: PayRateCardForm): PaymentCardDto => ({
  _id: card._id,
  initialId: card.initialId,
  name: card.name,
  effectiveFrom: buildEffectiveFrom(card) ?? '',
  rates: card.rates
    .filter((entry) => !entry.isDeleted)
    .map((entry) => ({
      level: entry.level,
      eventTypeId: entry.eventTypeId,
      rate: String(entry.rate || 0),
      rateType: entry.rateType
    }))
})

const formToApiPayload = (
  formCards: PayRateCardForm[],
  originalCards: PayRateCardForm[]
): PaymentCardDto[] => {
  const originalDtoById = new Map(
    originalCards
      .filter((card) => card._id)
      .map((card) => [card._id, JSON.stringify(cardToDto(card))])
  )

  return formCards
    .filter((card) => {
      if (card.initialId && card.plannedUpdateRemoved) {
        return false
      }

      if (card.isDeleted && !card._id) {
        return false
      }

      if (card.isDeleted || !card._id) {
        return true
      }

      const originalJson = originalDtoById.get(card._id)
      return !originalJson || JSON.stringify(cardToDto(card)) !== originalJson
    })
    .map((card) => {
      if (card.isDeleted) {
        return {
          _id: card._id,
          name: card.name,
          effectiveFrom: buildEffectiveFrom(card) || '',
          rates: [],
          deleted: true
        }
      }
      return cardToDto(card)
    })
}

const PayRatesSettings = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const sendEvent = useMixpanelEvent()
  const [form] = AntdForm.useForm()
  const [isCardsDirty, setIsCardsDirty] = useState(false)
  const cardsBottomRef = useRef<HTMLDivElement>(null)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [saveKey, setSaveKey] = useState(0)
  const [unsavedToggle, setUnsavedToggle] = useState<boolean | null>(null)
  const [unsavedChargeToggle, setUnsavedChargeToggle] = useState<
    boolean | null
  >(null)
  const [originalCardByCardId, setOriginalCardByCardId] = useState<
    Record<string, PayRateCardForm>
  >({})

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative: string } } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )
  const nominative = getNominative(preferredPeopleNominative, 'Service User')

  const flags = useFeatureFlags({ flagNames: ['rota_invoicing'] })?.data
  const rotaInvoicingEnabled = flags.rota_invoicing.enabled

  const { data: invoicingSettings } = useGetInvoicingSettings()
  const { updateInvoicingSettings } = useUpdateInvoicingSettings()

  const chargeRatesEnabled =
    unsavedChargeToggle !== null
      ? unsavedChargeToggle
      : (invoicingSettings?.enabled ?? false)

  const isDirty =
    unsavedToggle !== null || unsavedChargeToggle !== null || isCardsDirty

  const { data: payRatesSettings, isLoading: isSettingsLoading } =
    useGetPayRatesSettings()
  const { updatePayRatesSettings } = useUpdatePayRatesSettings()

  const payRatesEnabled =
    unsavedToggle !== null
      ? unsavedToggle
      : (payRatesSettings?.enabled ?? false)

  const {
    data: paymentCards,
    isLoading: isCardsLoading,
    refetch: refetchPaymentCards
  } = useGetPaymentCards(!isSettingsLoading && payRatesEnabled)
  const { updatePaymentCards } = useUpdatePaymentCards()
  const { deletePayRateCardFutureVersion } = useDeletePayRateCardFutureVersion()

  const initialValues = useMemo<PayRatesSettingsForm>(
    () => ({
      payRates: paymentCards?.length ? parseCards(paymentCards) : []
    }),
    [paymentCards]
  )

  const watchedPayRates = useWatch('payRates', form) as
    | PayRateCardForm[]
    | undefined

  const handlePublishedOnlySnapshot = (card: PayRateCardForm) => {
    const id = card._id
    if (!id) {
      return
    }
    setOriginalCardByCardId((prev) => ({
      ...prev,
      [id]: cloneDeep(card)
    }))
  }

  const handleSave = async () => {
    try {
      setIsUpdateLoading(true)

      if (payRatesEnabled && isCardsDirty) {
        await form.validateFields()
      }

      if (unsavedToggle !== null) {
        await updatePayRatesSettings({ enabled: payRatesEnabled })
      }

      if (payRatesEnabled && isCardsDirty) {
        const values = form.getFieldsValue(true) as PayRatesSettingsForm
        const futureVersionDeleteInitialIds = [
          ...new Set(
            values.payRates
              .filter((card) => card.initialId && card.plannedUpdateRemoved)
              .map((card) => card.initialId as string)
          )
        ]
        for (const initialId of futureVersionDeleteInitialIds) {
          await deletePayRateCardFutureVersion({ initialId })
        }
        const payload = formToApiPayload(
          values.payRates,
          initialValues.payRates
        )
        if (payload.length > 0) {
          await updatePaymentCards({ cards: payload })

          const addedCards = payload.filter(
            (card) => !card._id && !card.deleted
          )
          const editedCards = payload.filter(
            (card) => card._id && !card.deleted
          )

          if (addedCards.length > 0) {
            sendEvent(MixpanelEvents.PAY_RATE_ADDED, {
              numberOfPayRatesAdded: addedCards.length
            })
          }
          for (const card of editedCards) {
            sendEvent(MixpanelEvents.PAY_RATE_EDITED, {
              payRateName: card.name,
              effectiveFrom: card.effectiveFrom
            })
          }
        }
        if (futureVersionDeleteInitialIds.length > 0 || payload.length > 0) {
          const { data: freshCards } = await refetchPaymentCards()
          if (freshCards) {
            form.setFieldsValue({ payRates: parseCards(freshCards) })
          }
        }
      }

      if (unsavedChargeToggle !== null) {
        await updateInvoicingSettings({ enabled: chargeRatesEnabled })
        sendEvent(MixpanelEvents.INVOICING_TOGGLED, {
          teamMemberName:
            `${user?.name?.first ?? ''} ${user?.name?.last ?? ''}`.trim(),
          invoicingEnabled: chargeRatesEnabled
        })
      }

      showToast({ title: t('PAY_RATE_SETTINGS_UPDATED') })
      setUnsavedToggle(null)
      setUnsavedChargeToggle(null)
      setIsCardsDirty(false)
      setOriginalCardByCardId({})
      setSaveKey((prev) => prev + 1)
    } catch (err: unknown) {
      const error = err as
        | {
            errorFields?: Parameters<typeof handleOnFinishFailed>[0]
            message?: string
          }
        | undefined
      if (error?.errorFields) {
        handleOnFinishFailed(error.errorFields)
        return
      }
      showToast({
        title: error?.message || t('SETTINGS_ERROR'),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const handleFormValuesChange = () => {
    setIsCardsDirty(true)
  }

  const markCardsDirty = () => {
    setIsCardsDirty(true)
  }

  const checkCardsDirty = () => {
    const currentCards = form.getFieldsValue(true).payRates as PayRateCardForm[]
    const changedCards = formToApiPayload(currentCards, initialValues.payRates)
    const hasPendingFutureVersionDelete = currentCards.some(
      (card) => card.initialId && card.plannedUpdateRemoved
    )
    setIsCardsDirty(changedCards.length > 0 || hasPendingFutureVersionDelete)
  }

  const filterDeletedFields = (fieldName: number) =>
    form.getFieldValue(['payRates', fieldName, 'isDeleted']) !== true

  const newPayRate: PayRateCardForm = {
    name: '',
    effectiveDate: undefined,
    effectiveTime: undefined,
    rates: [
      {
        level: OverrideLevel.BASE,
        eventTypeId: null,
        rate: undefined as unknown as number,
        rateType: RateType.HOURLY
      }
    ]
  }

  return (
    <>
      <StyledPageWrapper>
        <PageHeader
          title={rotaInvoicingEnabled ? t('FINANCE') : t('PAY_RATES')}
          subtitle={rotaInvoicingEnabled ? undefined : t('PAY_RATES_INFO')}
          moreInfoLink={rotaInvoicingEnabled ? undefined : HELP_ARTICLE_URL}
          sticky
        />
        {isSettingsLoading ? (
          <Spinner />
        ) : (
          <>
            {rotaInvoicingEnabled && (
              <SwitchCard>
                <EnableToggleRow>
                  <Text weight="bold">{t('ENABLE_CHARGE_RATES')}</Text>
                  <Switch
                    checked={chargeRatesEnabled}
                    onChange={setUnsavedChargeToggle}
                  />
                </EnableToggleRow>
                <SwitchSubText>
                  {t('CHARGE_RATES_INFO', { nominative })}
                </SwitchSubText>
                {chargeRatesEnabled && (
                  <Banner
                    color={colours.status.success}
                    text={
                      <BannerContent>
                        <BannerTextWrapper>
                          <BannerText>
                            {t('CHARGE_RATES_ENABLED_BANNER', { nominative })}
                          </BannerText>
                        </BannerTextWrapper>
                        <BannerCta>
                          <Button
                            variant="default"
                            onClick={() => navigate('/residents/list')}
                          >
                            {t('SERVICE_USER_LIST', { nominative })} &rarr;
                          </Button>
                        </BannerCta>
                      </BannerContent>
                    }
                  />
                )}
              </SwitchCard>
            )}

            <SwitchCard>
              <EnableToggleRow>
                <Text weight="bold">{t('ENABLE_PAY_RATES')}</Text>
                <Switch checked={payRatesEnabled} onChange={setUnsavedToggle} />
              </EnableToggleRow>
              {rotaInvoicingEnabled && (
                <SwitchSubText>{t('PAY_RATES_INFO')}</SwitchSubText>
              )}
            </SwitchCard>

            {payRatesEnabled && !isCardsLoading && (
              <Form<PayRatesSettingsForm>
                form={form}
                onValuesChange={handleFormValuesChange}
                initialValues={initialValues}
                validateTrigger={['onBlur', 'onChange']}
              >
                <AntdForm.List name="payRates">
                  {(fields, { add }) => (
                    <FormCard
                      title={t('RATE_TYPES')}
                      rightChildren={
                        <Button
                          type="button"
                          variant="text"
                          icon={<Add />}
                          onClick={() => {
                            markCardsDirty()
                            add(newPayRate)
                            setTimeout(
                              () =>
                                cardsBottomRef.current?.scrollIntoView({
                                  behavior: 'smooth'
                                }),
                              0
                            )
                          }}
                        >
                          {t('ADD_A_NEW_PAY_RATE')}
                        </Button>
                      }
                    >
                      {!paymentCards?.length &&
                        fields.filter((field) =>
                          filterDeletedFields(field.name)
                        ).length > 0 && (
                          <WarningBox>
                            <Text>{t('PAY_RATES_FIRST_SAVE_WARNING')}</Text>
                          </WarningBox>
                        )}
                      {fields.filter((field) => filterDeletedFields(field.name))
                        .length === 0 && (
                        <EmptyText>{t('NO_RATE_TYPES')}</EmptyText>
                      )}
                      {fields
                        .filter((field) => filterDeletedFields(field.name))
                        .map((field) => {
                          const cardId = watchedPayRates?.[field.name]?._id
                          const originalCard =
                            cardId && originalCardByCardId[cardId]
                              ? originalCardByCardId[cardId]
                              : initialValues.payRates[field.name]
                          return (
                            <PayRateCard
                              key={`${field.key}-${saveKey}`}
                              fieldName={field.name}
                              form={form}
                              onDirty={markCardsDirty}
                              onClean={checkCardsDirty}
                              onPublishedOnlySnapshot={
                                handlePublishedOnlySnapshot
                              }
                              originalCard={originalCard}
                            />
                          )
                        })}
                      <div ref={cardsBottomRef} />
                    </FormCard>
                  )}
                </AntdForm.List>
              </Form>
            )}
          </>
        )}
      </StyledPageWrapper>

      <FixedFooter
        isUpdateLoading={isUpdateLoading}
        isDirty={isDirty}
        handleSubmit={handleSave}
      />

      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('ROTA_SETTINGS_NAVIGATION_MODAL_CONTENT')}
      />
    </>
  )
}

const StyledPageWrapper = styled(PageWrapper)`
  margin-bottom: 48px;
  padding-left: 24px;
  padding-right: 24px;

  & > *:first-child {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`

const EnableToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyText = styled(Text)`
  text-align: center;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const WarningBox = styled.div`
  border-radius: 4px;
  border: 1px solid rgba(46, 185, 255, 0.2);
  background: rgba(46, 185, 255, 0.05);
  padding: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const SwitchCard = styled.div`
  padding: 24px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SwitchSubText = styled(Text)`
  display: block;
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-top: 4px;
`

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
  width: 100%;
  text-align: left;
`

const BannerTextWrapper = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const BannerText = styled(Text)`
  text-align: left;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const BannerCta = styled.div`
  flex-shrink: 0;
`

export default PayRatesSettings
